import React, { PropsWithChildren, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { RegisteredArea, SecuredFlag } from './types';
import { SECURITY_MODE_QUERY_PARAM, readSecurityModeStored, subscribeSecurityModeStored, writeSecurityModeStored } from './securityModeStore';

type Listener = () => void;

export class SecurityRegistry {
  private flags = new Map<string, SecuredFlag>();
  private areas = new Map<string, RegisteredArea>();
  private listeners = new Set<Listener>();
  private version = 0;
  private notifyScheduled = false;

  registerFlag(flag: SecuredFlag): void {
    this.flags.set(flag.id, flag);
    this.scheduleNotify();
  }

  registerArea(area: RegisteredArea): void {
    this.areas.set(area.id, area);
    this.scheduleNotify();
  }

  unregisterArea(id: string): void {
    if (this.areas.delete(id)) this.scheduleNotify();
  }

  getFlags(): SecuredFlag[] {
    return Array.from(this.flags.values());
  }

  getAreas(): RegisteredArea[] {
    return Array.from(this.areas.values());
  }

  getFlag(id: string): SecuredFlag | undefined {
    return this.flags.get(id);
  }

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.version;

  private scheduleNotify = () => {
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      this.version++;
      this.listeners.forEach(l => l());
    });
  };
}

interface SecurityModeContextType {
  registry: SecurityRegistry | null;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  /**
   * Keeps the registry filled without showing security mode: the assistant reads which areas are
   * hidden or read-only, and why, for users who are not allowed to see the overlay.
   */
  setCollecting: (v: boolean) => void;
  selectedAreaId: string | null;
  selectAreaId: (id: string | null) => void;
  selectionVersion: number;
}

const SecurityModeContext = React.createContext<SecurityModeContextType>({
  registry: null,
  enabled: false,
  setEnabled: () => {},
  setCollecting: () => {},
  selectedAreaId: null,
  selectAreaId: () => {},
  selectionVersion: 0,
});

const readStorage = readSecurityModeStored;
const writeStorage = writeSecurityModeStored;

export const SecurityModeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const auth = useContext(AuthenticatedUserContext);
  const [searchParams] = useSearchParams();
  const userIsAdmin = !!auth?.isAdmin?.();
  const impersonating = !!searchParams.get('runAs');
  const allowed = userIsAdmin || impersonating;
  const urlFlag = searchParams.get(SECURITY_MODE_QUERY_PARAM) === '1';

  const computeEnabled = (): boolean => {
    if (!allowed) return false;
    const stored = readStorage();
    if (stored === '0') return false;
    if (stored === '1') return true;
    return urlFlag;
  };

  const [enabled, setEnabledState] = useState(computeEnabled);

  useEffect(() => {
    if (allowed && urlFlag && readStorage() === null) {
      writeStorage(true);
      setEnabledState(true);
    }
  }, [allowed, urlFlag]);

  useEffect(() => {
    if (!allowed && enabled) setEnabledState(false);
  }, [allowed, enabled]);

  useEffect(() => subscribeSecurityModeStored(() => setEnabledState(computeEnabled())), [allowed, urlFlag]);

  const [collecting, setCollecting] = useState(false);
  // Rendering (outline boxes, overlay) depends on `enabled` alone; registration needs only a registry.
  // Keyed on `active`, so an admin toggling the overlay while collecting keeps the same instance and
  // its registrations.
  const active = enabled || collecting;
  const registry = useMemo(() => (active ? new SecurityRegistry() : null), [active]);

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const setEnabled = (v: boolean) => {
    const next = v && allowed;
    setEnabledState(next);
    writeStorage(next);
  };

  const selectAreaId = (id: string | null) => {
    setSelectedAreaId(id);
    setSelectionVersion(v => v + 1);
  };

  const value = useMemo(
    () => ({ registry, enabled, setEnabled, setCollecting, selectedAreaId, selectAreaId, selectionVersion }),
    [registry, enabled, allowed, selectedAreaId, selectionVersion],
  );

  return <SecurityModeContext.Provider value={value}>{children}</SecurityModeContext.Provider>;
};

export const useSecurityMode = () => useContext(SecurityModeContext);

export const useSecurityRegistryVersion = (): number => {
  const { registry } = useSecurityMode();
  return useSyncExternalStore(
    registry ? registry.subscribe : () => () => {},
    registry ? registry.getSnapshot : () => 0,
    () => 0,
  );
};